import {format} from 'date-fns';
import {cloneDeep, forOwn, get, isUndefined, isString} from 'lodash';
import {IntlShape} from 'react-intl';

import DateUtils from 'src/js/common/utils/date_utils';
import NumberUtils from 'src/js/common/utils/number_utils';
import TranslateUtils from 'src/js/common/utils/translate_utils';

const FORMATTED_PROPERTY_CORE = 'core';
const FORMATTED_PROPERTY_ITEM_TYPE = 'itemType';
const FORMATTED_PROPERTY_LABEL = 'label';
const FORMATTED_PROPERTY_PROPERTY = 'property';
const FORMATTED_PROPERTY_TYPE = 'type';
const FORMATTED_PROPERTY_TYPE_I18N_PARAMS = 'i18nParams';
const FORMATTED_PROPERTY_FORMATTER = 'formatter';
const FORMATTED_PROPERTY_PARAMETERS = 'parameters';
const FORMATTED_PROPERTY_LOCALIZABLE = 'localizable';
export const FORMATTED_PROPERTY_PARAM_DECIMALS = 'decimals';
export const DEFAULT_FORMATTED_PROPERTY_PARAM_DECIMALS = 2;
const DEFAULT_FORMATTED_PROPERTY_LOCALE = 'cs';
const DEFAULT_FORMATTED_PROPERTY_DATE_FORMAT = 'dd. MM. yyyy';
const DEFAULT_FORMATTED_PROPERTY_DATETIME_FORMAT = 'dd. MM. yyyy HH:mm:ss';
const DEFAULT_FORMATTED_PROPERTY_MONTH_YEAR_FORMAT = 'MM/yyyy';

export const FORMATTED_PROPERTY_TYPE_ARRAY = 'array';
export const FORMATTED_PROPERTY_TYPE_BOOL = 'bool';
export const FORMATTED_PROPERTY_TYPE_DATE = 'date';
export const FORMATTED_PROPERTY_TYPE_DATETIME = 'datetime';
export const FORMATTED_PROPERTY_TYPE_DECIMAL = 'decimal';
export const FORMATTED_PROPERTY_TYPE_INTEGER = 'integer';
export const FORMATTED_PROPERTY_TYPE_JSON = 'json';
export const FORMATTED_PROPERTY_TYPE_MONTH_YEAR = 'month_year';
export const FORMATTED_PROPERTY_TYPE_NUMBER = 'number';
export const FORMATTED_PROPERTY_TYPE_PERCENT = 'percent';
export const FORMATTED_PROPERTY_TYPE_STRING = 'string';
export const FORMATTED_PROPERTY_TYPE_TEXT = 'text';

const FORMATTED_PROPERTY_FORMATTER_NO_FORMAT = 'noFormat';
const FORMATTED_PROPERTY_FORMATTER_FILE_SIZE = 'fileSize';

const FILE_SIZE_KB = 1024;
const FILE_SIZE_MB = 1048576;
const FILE_SIZE_GB = 1073741824;
const NUMBER_INTEGRAL_PART_REGEXP = /\B(?=(\d{3})+(?!\d))/g;

export class FormattedProperty {
  static formatFileSize(value: number, translateService?: IntlShape): string {
    let formattedValue;
    const locale = translateService
      ? translateService.locale
      : DEFAULT_FORMATTED_PROPERTY_LOCALE;
    if (value >= FILE_SIZE_GB) {
      formattedValue = `${Number(value / FILE_SIZE_GB).toLocaleString(
        locale
      )} GB`;
    } else if (value >= FILE_SIZE_MB) {
      formattedValue = `${Number(value / FILE_SIZE_MB).toLocaleString(
        locale
      )} MB`;
    } else if (value >= FILE_SIZE_KB) {
      formattedValue = `${Number(value / FILE_SIZE_KB).toLocaleString(
        locale
      )} kB`;
    } else {
      formattedValue = `${Number(value).toLocaleString(locale)} B`;
    }
    return formattedValue;
  }

  static formatDecimal(value, wantedDecimals, translateService?: IntlShape) {
    const number =
      !Number.isNaN(parseFloat(value)) && Number.isFinite(value)
        ? value
        : Number(value);
    // round first to get wanted number
    const roundedNumber = NumberUtils.roundToWantedDecimals(
      number,
      wantedDecimals
    );
    // get integer part
    const integerNumber = Math.trunc(roundedNumber);
    // get decimal part (round second due the floating point arithmetic)
    const decimalNumber = NumberUtils.roundToWantedDecimals(
      roundedNumber - integerNumber,
      wantedDecimals
    );
    const locale = translateService
      ? translateService.locale
      : DEFAULT_FORMATTED_PROPERTY_LOCALE;
    // format integralPart
    const formattedIntegralPart = integerNumber
      .toString()
      .replace(NUMBER_INTEGRAL_PART_REGEXP, ' ');
    // format decimal number and remove leading zero
    const formattedDecimalPart =
      decimalNumber === 0
        ? ''
        : Number(decimalNumber)
            .toLocaleString(locale, {
              maximumFractionDigits: wantedDecimals,
            })
            .substring(1);
    return `${formattedIntegralPart}${formattedDecimalPart}`;
  }

  static formatValue(propertyDefinition, value, translateService?: IntlShape) {
    let formattedValue = value !== null && value !== undefined ? value : '';
    if (propertyDefinition && value !== null) {
      const propertyType =
        propertyDefinition.getType() === FORMATTED_PROPERTY_TYPE_ARRAY
          ? propertyDefinition.getItemType()
          : propertyDefinition.getFormatter() || propertyDefinition.getType();
      switch (propertyType) {
        case FORMATTED_PROPERTY_TYPE_ARRAY: {
          formattedValue = value;
          break;
        }
        case FORMATTED_PROPERTY_TYPE_BOOL: {
          formattedValue = Boolean(value).toString();
          break;
        }
        case FORMATTED_PROPERTY_TYPE_DATE: {
          if (formattedValue !== '') {
            // replace space between date and time part with 'T'
            const valueToFormat =
              value && isString(value) ? value.trim().replace(' ', 'T') : value;
            if (translateService) {
              formattedValue = translateService.formatDate(
                new Date(valueToFormat)
              );
            } else {
              formattedValue = format(
                DateUtils.resetTime(valueToFormat),
                DEFAULT_FORMATTED_PROPERTY_DATE_FORMAT
              );
            }
          }
          break;
        }
        case FORMATTED_PROPERTY_TYPE_DATETIME: {
          if (formattedValue !== '') {
            // replace space between date and time part with 'T'
            const valueToFormat =
              value && isString(value) ? value.trim().replace(' ', 'T') : value;
            if (translateService) {
              const datePart = translateService.formatDate(
                new Date(valueToFormat)
              );
              const timePart = translateService.formatTime(
                new Date(valueToFormat)
              );
              formattedValue = `${datePart} ${timePart}`;
            } else {
              formattedValue = format(
                valueToFormat,
                DEFAULT_FORMATTED_PROPERTY_DATETIME_FORMAT
              );
            }
          }
          break;
        }
        case FORMATTED_PROPERTY_TYPE_DECIMAL: {
          const wantedDecimals =
            propertyDefinition.getParameters() &&
            propertyDefinition.getParameters()[
              FORMATTED_PROPERTY_PARAM_DECIMALS
            ]
              ? propertyDefinition.getParameters()[
                  FORMATTED_PROPERTY_PARAM_DECIMALS
                ]
              : DEFAULT_FORMATTED_PROPERTY_PARAM_DECIMALS;
          formattedValue = FormattedProperty.formatDecimal(
            value,
            wantedDecimals,
            translateService
          );
          break;
        }
        case FORMATTED_PROPERTY_TYPE_INTEGER: {
          const number =
            !Number.isNaN(parseFloat(value)) && Number.isFinite(value)
              ? value
              : Number(value);
          const integerNumber = Math.trunc(number);
          formattedValue = integerNumber
            .toString()
            .replace(NUMBER_INTEGRAL_PART_REGEXP, ' ');
          break;
        }
        case FORMATTED_PROPERTY_TYPE_JSON: {
          formattedValue = JSON.stringify(value, null, 2);
          break;
        }
        case FORMATTED_PROPERTY_TYPE_MONTH_YEAR: {
          if (formattedValue !== '') {
            // replace space between date and time part with 'T'
            const valueToFormat =
              value && isString(value)
                ? value.trim().replace(' ', 'T').replace('/', '-')
                : value;
            if (translateService) {
              formattedValue = translateService.formatDate(
                DateUtils.resetTime(valueToFormat),
                {
                  year: 'numeric',
                  month: 'long',
                }
              );
            } else {
              formattedValue = format(
                DateUtils.resetTime(valueToFormat),
                DEFAULT_FORMATTED_PROPERTY_MONTH_YEAR_FORMAT
              );
            }
          }
          break;
        }
        case FORMATTED_PROPERTY_TYPE_NUMBER: {
          const locale = translateService
            ? translateService.locale
            : DEFAULT_FORMATTED_PROPERTY_LOCALE;
          formattedValue = Number(value).toLocaleString(locale);
          break;
        }
        case FORMATTED_PROPERTY_TYPE_PERCENT: {
          const wantedDecimals =
            propertyDefinition.getParameters() &&
            propertyDefinition.getParameters()[
              FORMATTED_PROPERTY_PARAM_DECIMALS
            ]
              ? propertyDefinition.getParameters()[
                  FORMATTED_PROPERTY_PARAM_DECIMALS
                ]
              : DEFAULT_FORMATTED_PROPERTY_PARAM_DECIMALS;
          const decimalNumber = FormattedProperty.formatDecimal(
            value * 100,
            wantedDecimals,
            translateService
          );
          formattedValue = `${decimalNumber}%`;
          break;
        }
        case FORMATTED_PROPERTY_FORMATTER_FILE_SIZE: {
          formattedValue = FormattedProperty.formatFileSize(
            value,
            translateService
          );
          break;
        }
        case FORMATTED_PROPERTY_FORMATTER_NO_FORMAT:
        case FORMATTED_PROPERTY_TYPE_STRING:
        case FORMATTED_PROPERTY_TYPE_TEXT: {
          const stringValue = value ? value.toString() : '';
          if (stringValue !== '') {
            formattedValue =
              translateService &&
              TranslateUtils.hasTranslation(stringValue, translateService)
                ? TranslateUtils.translate(stringValue, translateService)
                : stringValue;
          }
          break;
        }
      }
    }
    return formattedValue || '';
  }

  /**
   * Translate formatted label and its params.
   * @param value
   * @param intl
   * @param labelParamsData
   * @param labelParamsDefinition
   * @returns formatted label
   */
  static formatLabel(
    value: string,
    intl: IntlShape,
    labelParamsData: Record<string, any> = {},
    labelParamsDefinition: Record<string, any> = {}
  ): string {
    let label = value;
    if (TranslateUtils.hasTranslation(value, intl)) {
      const formattedLabelParams = {};
      // format data properties according to the definition in labelParamsDefinition
      forOwn(labelParamsDefinition, (item, key) => {
        const propDefinition = new FormattedProperty(item);
        let propData;
        // project property need to be found in "properties" object
        if (item.isProjectProperty && labelParamsData.properties) {
          const projectProperty = labelParamsData.properties.find(
            (it) => it.typeName === key
          );
          propData = projectProperty ? projectProperty.value : undefined;
        } else {
          propData = labelParamsData[key];
        }
        // if the value of param was formatted in some previous step,
        // it is formatted only if there is defined value
        formattedLabelParams[item.property] =
          !isUndefined(formattedLabelParams[item.property]) &&
          isUndefined(propData)
            ? formattedLabelParams[item.property]
            : FormattedProperty.formatValue(propDefinition, propData, intl);
      });
      // translate label with use of formatted properties
      label = TranslateUtils.translate(value, intl, formattedLabelParams);
    }
    return label;
  }

  static getObjectProperty(dataObject, propertyName) {
    let value;
    if (propertyName.indexOf('.') === -1) {
      value = dataObject[propertyName];
    } else {
      value = get(dataObject, propertyName);
    }
    return value;
  }

  private readonly rawData;

  constructor(rawData = {}) {
    this.rawData = {...rawData};
  }

  clone() {
    return Object.assign(new FormattedProperty(cloneDeep(this.rawData)), this);
  }

  getLabel() {
    return (
      this.rawData[FORMATTED_PROPERTY_LABEL] ||
      this.rawData[FORMATTED_PROPERTY_PROPERTY]
    );
  }

  getProperty() {
    return this.rawData[FORMATTED_PROPERTY_PROPERTY];
  }

  getI18nParams() {
    return this.rawData[FORMATTED_PROPERTY_TYPE_I18N_PARAMS];
  }

  getRawData() {
    return this.rawData;
  }

  getType() {
    return this.rawData[FORMATTED_PROPERTY_TYPE];
  }

  getCore() {
    return this.rawData[FORMATTED_PROPERTY_CORE];
  }

  getItemType() {
    return this.rawData[FORMATTED_PROPERTY_ITEM_TYPE];
  }

  getFormatter() {
    return this.rawData[FORMATTED_PROPERTY_FORMATTER];
  }

  getParameters() {
    return this.rawData[FORMATTED_PROPERTY_PARAMETERS];
  }

  getLocalizable() {
    return this.rawData[FORMATTED_PROPERTY_LOCALIZABLE];
  }
}

export default {FormattedProperty};
